import GreenTick from '@/assets/Icons/components/check-box/GreenTick';
import { useState, useEffect, useRef } from 'react';
import CustomTextInput from './CustomTextInput';
import UserCustomerTextArea from './UserCustomerTextArea';
import { CustomConditionInputProps } from './types/CustomInputsTypes';
import CustomDataTable from './CustomDataTable';
import CustomMultipleFileUpload from './CustomMultipleFileUpload';
import { trackEvent } from '@/utils/track-event';
import { PostHogEvents } from '@/utils/PostHogEvents';
import { PostHogMessages } from '@/utils/PostHogMessages';

const CustomConditionInput = ({
  sectionData,
  label,
  options,
  onChange,
  required = false,
  description,
  isLockedByMe,
  lockedOwner,
  isReviewForm,
  defaultValue,
  value,
  onFocus,
  isPdf = false,
  isCancel = false,
  setIsCancel,
  field,
  shouldHighlightOption,
  shouldHighlightNestedField,
}: CustomConditionInputProps) => {
  // Working state (what's shown in the UI)
  const [selectedOption, setSelectedOption] = useState<string | null>();
  const [nestedFieldValue, setNestedFieldValue] = useState<any>('');
  const [tableResponseData, setTableResponseData] = useState<any>(null);

  // Original state (what we revert to on cancel)
  const originalValues = useRef({
    selectedOption: null as string | null | undefined,
    nestedFieldValue: '',
    tableResponseData: null as any,
  });

  const [conditionFieldId, setConditionFieldId] = useState<string>('');
  const [originalTableResponse, setOriginalTableResponse] = useState<any>(null);

  useEffect(() => {
    const title = sectionData?.name;
    const formId = sectionData?.sectionId;
    if (title) {
      document.title = `${title} | Plaintiff Fact Sheet`;
    }
    trackEvent(PostHogEvents.FORM_SECTION_VIEWED, {
      section_name: title,
      form_id: formId,
      timestamp: new Date().toISOString(),
      message: PostHogMessages[PostHogEvents.FORM_SECTION_VIEWED],
    });
  }, [sectionData?.name]);

  useEffect(() => {
    if (sectionData?.fields) {
      const conditionField = sectionData.fields.find(
        (field: any) =>
          field.label === label &&
          (field.field_type === 'condition' || field.field_type === 'condition_with_table')
      );

      if (conditionField) {
        setConditionFieldId(conditionField.field_id);
        const initialValue = value ? value : defaultValue;

        if (typeof initialValue === 'string') {
          // Set both working state and original values
          setSelectedOption(initialValue);
          originalValues.current.selectedOption = initialValue;

          const selectedOptionObj: any = options.find((opt) => opt.value === initialValue);
          if (selectedOptionObj?.field) {
            setNestedFieldValue(selectedOptionObj.field.value || '');
            originalValues.current.nestedFieldValue = selectedOptionObj.field.value || '';
          }
          if (selectedOptionObj?.table_responses) {
            setTableResponseData(selectedOptionObj.table_responses);
            setOriginalTableResponse(selectedOptionObj.table_responses);
            originalValues.current.tableResponseData = selectedOptionObj.table_responses;
          }
        } else {
          // Reset all states if no initial value
          setSelectedOption(null);
          setNestedFieldValue('');
          setTableResponseData(null);
          setOriginalTableResponse(null);
          originalValues.current = {
            selectedOption: null,
            nestedFieldValue: '',
            tableResponseData: null,
          };
        }
      }
    }
  }, [sectionData, label, options, defaultValue, conditionFieldId]);

  // Handle cancel - revert to original values
  useEffect(() => {
    if (isCancel) {
      setSelectedOption(originalValues.current.selectedOption);
      setNestedFieldValue(originalValues.current.nestedFieldValue);
      setTableResponseData(originalValues.current.tableResponseData);
      setOriginalTableResponse(originalValues.current.tableResponseData);
      if (setIsCancel) {
        setIsCancel(false);
      }
    }
  }, [isCancel]);

  const handleOptionSelect = (value: string) => {
    const selectedOptionObj: any = options.find((opt) => opt.value === value);

    if (!conditionFieldId) {
      console.warn('Condition field ID not initialized yet');
      return;
    }

    // Update working state
    setSelectedOption(value);

    // Reset nested field value when changing options
    setNestedFieldValue('');

    // Handle table data
    if (!selectedOptionObj?.table_responses) {
      setTableResponseData(null);
      setOriginalTableResponse(null);
    } else {
      setTableResponseData(selectedOptionObj.table_responses);
      setOriginalTableResponse(selectedOptionObj.table_responses);
    }

    const updateObj: any = {
      value: value,
    };

    if (selectedOptionObj?.field) {
      updateObj.nested_field = {
        field_id: selectedOptionObj.field.field_id,
        value: '',
      };
    }

    if (selectedOptionObj?.table_responses) {
      updateObj.table_responses = selectedOptionObj.table_responses;
    }

    if (onChange) {
      onChange(conditionFieldId, updateObj);
    }
  };

  const handleNestedFieldChange = (fieldId: string, value: any) => {
    setNestedFieldValue(value);

    if (onChange && selectedOption && conditionFieldId) {
      const selectedOptionObj: any = options.find((opt) => opt.value === selectedOption);

      if (selectedOptionObj?.field) {
        if (selectedOptionObj.field.field_type === 'file' && value) {
          if (value.files && Array.isArray(value.files)) {
            onChange(conditionFieldId, {
              value: selectedOption,
              nested_field: {
                field_id: fieldId,
                value: value.value || '',
                files: value.files.map((file: any) => ({
                  blobUrl: file.blobUrl,
                  fileName: file.fileName,
                  fileId: file.fileId,
                  fileSize: file.fileSize,
                  description: file.description,
                })),
              },
            });
          } else {
            const fileValue = value.blobUrl ? value.blobUrl : value;
            onChange(conditionFieldId, {
              value: selectedOption,
              nested_field: {
                field_id: fieldId,
                value: fileValue,
                file_metadata: value.file
                  ? {
                      file_name: value.fileName || value.file.name,
                      file_size: value.fileSize || value.file.size,
                      file_type: value.fileType || value.file.type,
                    }
                  : undefined,
              },
            });
          }
        } else {
          onChange(conditionFieldId, {
            value: selectedOption,
            nested_field: {
              field_id: fieldId,
              value: value,
            },
          });
        }
      }
    }
  };

  const handleTableDataChange = (tableData: any) => {
    setTableResponseData(tableData);

    if (!conditionFieldId || !selectedOption) {
      console.error('Cannot update table data: Missing condition field ID or selected option');
      return;
    }

    const selectedOptionObj: any = options.find((opt) => opt.value === selectedOption);
    if (!selectedOptionObj) return;

    const formattedValues = tableData.values.map((cell: any) => ({
      ...cell,
      row_id: '',
    }));

    if (onChange) {
      onChange(conditionFieldId, {
        value: selectedOption,
        nested_table: {
          table_id: tableData.table_id,
          values: formattedValues,
        },
      });
    }
  };

  const renderNestedField = () => {
    if (!selectedOption) return null;

    const selectedOptionObj = options.find((opt) => opt.value === selectedOption);
    if (!selectedOptionObj?.field) return null;

    const nestedField = selectedOptionObj.field;

    // Check if this specific nested field should be highlighted
    const shouldHighlight = shouldHighlightNestedField?.(nestedField, field);
    const nestedHighlightClass = shouldHighlight
      ? 'ring-2 ring-[#EF8B21] bg-[#FCF2E6] p-2 rounded-md'
      : '';

    const handleFocus = () => {
      trackEvent(PostHogEvents.FORM_FIELD_FOCUSED, {
        field_name: nestedField.label,
        form_id: nestedField.field_id,
        message: PostHogMessages[PostHogEvents.FORM_FIELD_FOCUSED],
      });
    };

    if (!nestedField) return null;

    switch (nestedField.field_type) {
      case 'text_input':
        return (
          <div className={nestedHighlightClass}>
            <CustomTextInput
              label={''}
              name={nestedField.field_id}
              value={nestedFieldValue}
              onChange={(e) => handleNestedFieldChange(nestedField.field_id, e.target.value)}
              placeholder={nestedField.additional_config?.placeholder || ''}
              required={false}
              width="w-full"
              isEditable={nestedField.additional_config?.editable}
              isLockedByMe={isLockedByMe}
              lockedOwner={lockedOwner}
              isReviewForm={isReviewForm}
              onFocus={handleFocus}
            />
          </div>
        );
      case 'text_area':
        return (
          <div className={nestedHighlightClass}>
            <UserCustomerTextArea
              name={nestedField.field_id}
              value={nestedFieldValue}
              label="Description"
              onChange={(e) => handleNestedFieldChange(nestedField.field_id, e.target.value)}
              placeholder={nestedField.additional_config?.placeholder || ''}
              required={nestedField.is_required}
              description={nestedField?.additional_config?.helpText}
              isCondition={true}
              className="w-full"
              rows={8}
              isLockedByMe={isLockedByMe}
              lockedOwner={lockedOwner}
              isReviewForm={isReviewForm}
              onFocus={handleFocus}
              isPdf={isPdf}
            />
          </div>
        );
      case 'file':
        return (
          <div className={nestedHighlightClass}>
            <CustomMultipleFileUpload
              value={nestedFieldValue}
              name={nestedField.field_id}
              onChange={(fileData) => handleNestedFieldChange(nestedField.field_id, fileData)}
              required={nestedField.is_required}
              isCondition={true}
              isLockedByMe={isLockedByMe}
              lockedOwner={lockedOwner}
              isReviewForm={isReviewForm}
              values={nestedField?.files}
              onFocus={handleFocus}
              isPdf={isPdf}
              isCancel={isCancel}
            />
          </div>
        );
      default:
        return null;
    }
  };

  // Highlight class for the main Yes/No toggle area ONLY
  // const mainFieldHighlightClass = highlightMainField
  //   ? 'ring-2 ring-[#EF8B21] bg-[#FCF2E6] p-2 rounded-md'
  //   : '';

  return (
    <div className="w-full">
      <div className="mb-2 border-b py-4">
        <label className="block font-semibold text-ds-gray-800 label-text">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        {description && (
          <p className="text-xs text-ds-gray-400 mt-1 confirmation-note">{description}</p>
        )}
      </div>

      {/* Apply highlighting ONLY to the Yes/No toggle section if main field needs highlighting */}
      <div
        className={`flex items-center space-x-2 ${shouldHighlightOption?.(field) ? 'ring-2 ring-[#EF8B21] bg-[#FCF2E6] p-2 rounded-md' : ''}`}
      >
        {options.map((option: any) => {
          return (
            <div key={option.value} className={`flex items-center mr-4`}>
              <button
                className={`flex items-center justify-center rounded-full w-4 h-4 ${isPdf ? 'mt-4' : ''} ${
                  isReviewForm ? 'cursor-default' : 'cursor-pointer'
                } ${
                  selectedOption === option.value
                    ? 'bg-ds-brand-mc-green-2 text-white'
                    : 'border border-gray-300'
                }`}
                onClick={() => {
                  handleOptionSelect(option.value);
                  onFocus?.();
                }}
                aria-pressed={selectedOption === option.value}
                role="radio"
                type="button"
                disabled={isReviewForm || (lockedOwner !== '' && !isLockedByMe)}
              >
                {selectedOption === option.value && <GreenTick />}
              </button>
              <span className="text-sm ml-2 text-gray-500">{option.label}</span>
            </div>
          );
        })}
      </div>

      {/* Nested field rendering with individual highlighting - NO className prop here */}
      {selectedOption && options.find((opt) => opt.value === selectedOption)?.field && (
        <div className="mt-4">{renderNestedField()}</div>
      )}

      {selectedOption && tableResponseData && (
        <div className="mt-4">
          <CustomDataTable
            tableData={originalTableResponse}
            onTableDataChange={handleTableDataChange}
            isReviewForm={isReviewForm}
            isLockedByMe={isLockedByMe}
            lockedOwner={lockedOwner}
            alwaysEditable={true}
            isPdf={isPdf}
            isLabelShow={false}
            isDeleteIconShow={true}
            isCancel={isCancel}
            onCancel={() => setIsCancel?.(false)}
          />
        </div>
      )}
    </div>
  );
};

export default CustomConditionInput;
